import { isCardTypeValidForCurrency } from "@/shared/payment/utils";
import type { PaymentInstrumentDetailsType } from "@/core/api/graphql/types";

export const getNonDefaultPaymentInstrumentsWithSameDataAreaId = (
  payments: PaymentInstrumentDetailsType[] | undefined,
  paymentInstrumentTokenId: string,
  dataAreaId: string,
): PaymentInstrumentDetailsType[] | undefined => {
  return payments?.filter((card) => {
    const isIdDifferent = card.paymentInstrumentTokenId !== paymentInstrumentTokenId;
    const candidateCardDataAreaId = card.dataAreaId;
    return isIdDifferent && candidateCardDataAreaId === dataAreaId;
  });
};

export const getInvalidCardMessageByCurrency = (currencyCode: string, t: (key: string) => string): string => {
  return currencyCode === "USD" ? t("common.messages.invalid_card_type_usd") : t("common.messages.invalid_card_type");
};

const isCardTypeSupported = (cardType: string, currencyCode: string, cardTypesMap: Record<string, string>): boolean => {
  return (
    Object.values(cardTypesMap)
      .map((value) => value.toLowerCase())
      .includes(cardType.toLowerCase()) && isCardTypeValidForCurrency(cardType, currencyCode)
  );
};

export const isCardValid = (
  card: PaymentInstrumentDetailsType | undefined,
  currencyCode: string,
  cardTypesMap: Record<string, string>,
): boolean => {
  return (
    !!card &&
    !!card.billingAddress &&
    !!card.cardType &&
    isCardTypeSupported(card.cardType, currencyCode, cardTypesMap) &&
    !card.isTenantChangeNeeded
  );
};

export const getCardUnavailableMessage = (
  card: PaymentInstrumentDetailsType,
  currencyCode: string,
  cardTypesMap: Record<string, string>,
  t: (key: string) => string,
): string => {
  if (card.validationStatus === "BILLING_ADDRESS_ID_NULL") {
    return t("pages.account.payments.billing_address_id_null");
  }

  if (!!card.billingAddress && card.cardType && !isCardTypeSupported(card.cardType, currencyCode, cardTypesMap)) {
    return getInvalidCardMessageByCurrency(currencyCode, t);
  }

  if (card.isTenantChangeNeeded) {
    return t("pages.account.payments.tenant_change_needed");
  }

  return t("pages.account.payments.no_match_for_billing_address_id");
};
